import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, first, tap} from 'rxjs/operators';
import { of } from 'rxjs';
import * as PicklistsActions from '../actions/picklists.actions';
import { Store } from '@ngrx/store';
import { CategoriesService } from '@app/services/categories/categories.service';
import { AreasService } from '@app/services/areas/areas.service';
import { selectPicklistsDirectionState, selectPicklistsSortState, selectPicklistsState } from '../selectors/picklists.selectors';
import { ToastrService } from 'ngx-toastr';
import * as CoreActions from '@app/core/store/core.action';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { PicklistsService } from '@app/services/picklists/picklist.service';

@Injectable()
export class PicklistsEffects {

  loadPicklists$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PicklistsActions.loadPicklists),
      switchMap(action => {
        return this._picklists.getPicklists('pickListID', 'desc', action.query, 0, 100, action.templates).pipe(
          map((data): any => {
            if(data.result!=null && data.result!='undefined' && data.result=='failed.access' && this.location.path()=='/picklists'){
              this.store$.dispatch(CoreActions.showPermissionMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.access')
              }));
            }

            return PicklistsActions.loadPicklistsSuccess({ picklists: data.picklists, total: data.total })
          }),
          catchError(error => {
            return of(PicklistsActions.loadPicklistsFailure({ error }))
          })
        )
      })
    );
  });

  searchPicklists$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PicklistsActions.search),
      switchMap(action => {
        let direction: string;
        let sort: string;
        // this.store$.select(selectPicklistsDirectionState).pipe(first()).subscribe(res => direction = res);
        // this.store$.select(selectPicklistsSortState).pipe(first()).subscribe(res => sort = res);

        return this._picklists.searchPicklists('pickListID', 'ASC', action.query).pipe(
          map((data): any => {
            return PicklistsActions.searchSuccess({ picklists: data.picklists, total: data.total })
          }),
          catchError(error => {
            return of(PicklistsActions.searchFailure({ error }))
          })
        )
      })
    );
  });

  createPicklist$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PicklistsActions.savePicklist),
      switchMap(action => {
        return this._picklists.onSave(action.form).pipe(
          map((response): any => {
            if(response.result!='undefined' && response.result=='failed.max'){
              this.store$.dispatch(CoreActions.showErrorMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.limitpicklist'),
                show_upgrade: true
              }));
              // Must return the action itself: wrapping it in of() dispatches
              // an Observable as an action and corrupts the store.
              return PicklistsActions.savePicklistFailure({ error: 'An error occurred.' })
            }

            if(response.error && response.message!=''){
              //return PicklistsActions.savePicklistSuccess(response)
              this._toastr.error(this._translation.instant(response.message));
              return PicklistsActions.savePicklistFailure({ error: 'An error occurred.' })
            }
            else{
              return PicklistsActions.savePicklistSuccess(response);
            }
          
            
          }),
          catchError(error => {
            return of(PicklistsActions.savePicklistFailure({ error }))
          })
        )
      })
    );
  });

  updatePicklist$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PicklistsActions.updatePicklist),
      switchMap(action => {
        return this._picklists.updatePicklist(action.form).pipe(
          map((response): any => {
            if(response.error && response.message!=''){
              this._toastr.error(this._translation.instant(response.message));
              return PicklistsActions.updatePicklistFailure({ error: 'An error occurred.' })
            }

            return PicklistsActions.updatePicklistSuccess(response)
          }),
          catchError(error => {
            return of(PicklistsActions.updatePicklistFailure({ error }))
          })
        )
      })
    );
  });

  loadPicklist$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PicklistsActions.loadPicklist),
      switchMap(action => {
        return this._picklists.getPicklist(action.pickListID).pipe(
          map(({ picklist }): any => {
            return PicklistsActions.loadPicklistSuccess({ picklist })
          }),
          catchError(error => {
            return of(PicklistsActions.loadPicklistFailure({ error }))
          })
        )
      })
    );
  });

  deletePicklists$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PicklistsActions.deletePicklists),
      switchMap(action => {
        return this._picklists.batchDelete(action.selected).pipe(
          map(() => {
            return PicklistsActions.deletePicklistsSuccess(action.query);}),
          catchError(error => {
            return of(PicklistsActions.deletePicklistsFailure({ error }))
          })
          )
      })
    );
  });

  refreshPicklistsAfterDelete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PicklistsActions.deletePicklistsSuccess),
      switchMap(action => {
        return of(0).pipe(
          map(() => PicklistsActions.loadPicklists({query:''})),
          catchError(error => of(PicklistsActions.deletePicklistsFailure({ error }))
          ))
      })
    );
  });

  cleanPicklistsAfterDelete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PicklistsActions.deletePicklistsSuccess),
      switchMap(action => {
        return of(0).pipe(
          map(() => PicklistsActions.setSelected({ selected: [] })),
          catchError(error => of(PicklistsActions.deletePicklistsFailure({ error }))
          ))
      })
    );
  });

  deletePicklist$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PicklistsActions.deletePicklist),
      switchMap(action => {
        return this._picklists.onDelete(action.pickListID).pipe(
          tap(() => {
            // this._toastr.error(`Successfully deleted picklist.`);
          }),
          map((): any => {
            return PicklistsActions.deletePicklistSuccess({ pickListID: action.pickListID })
          }),
          catchError(error => {
            return of(PicklistsActions.deletePicklistFailure({ error: error.message }))
          })
        )
      })
    );
  });


  setSort$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PicklistsActions.setSort),
      switchMap(action => {
        let templates: any = null;
        this.store$.select(selectPicklistsState).pipe(first()).subscribe(state => templates = state.templates);
        return this._picklists.getPicklists(action.sort, action.direction, action.query, 0, 100, templates).pipe(
          map((data): any => {
            return PicklistsActions.loadPicklistsSuccess({ picklists: data.picklists, total: data.total })
          })
        )
      })
    );
  });

  constructor(
    private actions$: Actions, private _picklists: PicklistsService,
    private store$: Store, private _categories: CategoriesService,
    private _areas: AreasService,
    private _toastr: ToastrService,
    public _router: Router,
    private location : Location,
    public _translation: TranslateService
  ) { }

}
